import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SITE } from '../../data/servers';
import { PAGES, articlePath } from '../../site/routes';
import { fetchArticle, fetchArticles, coverSrc, formatArticleDate, readingTime } from '../../site/articlesApi';
import Markdown, { extractHeadings } from '../../site/Markdown';
import ArticleCard, { ArticleCover } from '../../site/ArticleCard';
import CoinLoader from '../../components/CoinLoader';
import { usePageSeo, Breadcrumbs, Section, CtaBand, ArrowLink } from '../../site/ui';
import NotFoundPage from './NotFoundPage';

export default function ArticlePage() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [status, setStatus] = useState('loading'); // loading | ok | notfound | error
  const [related, setRelated] = useState([]);

  useEffect(() => {
    setStatus('loading');
    fetchArticle(slug)
      .then(a => { setArticle(a); setStatus('ok'); })
      .catch(e => setStatus(e.status === 404 ? 'notfound' : 'error'));
    fetchArticles().then(list => setRelated(list.filter(a => a.slug !== slug).slice(0, 3))).catch(() => {});
  }, [slug]);

  const breadcrumbs = article ? [
    { label: 'Accueil', to: PAGES.home },
    { label: 'Articles', to: PAGES.articles },
    { label: article.title, to: articlePath(article.slug) },
  ] : null;

  usePageSeo(status === 'ok' ? {
    title: `${article.title} | AV Kamas`,
    description: article.meta_description || article.excerpt,
    path: articlePath(article.slug),
    breadcrumbs,
    jsonLd: [{
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.title,
      description: article.meta_description || article.excerpt,
      datePublished: String(article.published_at).replace(' ', 'T'),
      dateModified: String(article.updated_at).replace(' ', 'T'),
      ...(article.cover_url ? { image: coverSrc(article.cover_url) } : {}),
      author: { '@type': 'Organization', name: SITE.name, url: SITE.url },
      publisher: { '@type': 'Organization', name: SITE.name, url: SITE.url },
      mainEntityOfPage: `${SITE.url}${articlePath(article.slug)}`,
    }],
  } : {
    title: status === 'notfound' ? 'Article introuvable | AV Kamas' : 'Articles | AV Kamas',
    path: articlePath(slug),
    noindex: status === 'notfound',
  });

  if (status === 'notfound') return <NotFoundPage embedded />;
  if (status !== 'ok') {
    return (
      <main className="bg-gray-950 text-white min-h-[60vh] max-w-3xl mx-auto px-4 sm:px-6 py-10">
        {status === 'error'
          ? <p className="text-red-400">Impossible de charger l'article pour le moment.</p>
          : <CoinLoader label="Chargement de l'article…" />}
      </main>
    );
  }

  return (
    <main className="bg-gray-950 text-white">
      <Breadcrumbs items={breadcrumbs} />
      <article className="max-w-3xl mx-auto px-4 sm:px-6 pt-4 sm:pt-6 pb-8">
        <header className="mb-6">
          <p className="text-xs sm:text-sm text-gray-500 mb-2">
            <span className="text-yellow-400 font-semibold">{article.category}</span>
            {' · '}<time dateTime={String(article.published_at).slice(0, 10)}>{formatArticleDate(article.published_at)}</time>
            {' · '}{readingTime(article.content)} min de lecture
          </p>
          <h1 className="text-2xl sm:text-4xl font-bold leading-tight mb-3">{article.title}</h1>
          {article.excerpt && <p className="text-base sm:text-lg text-gray-400 leading-relaxed">{article.excerpt}</p>}
        </header>
        {article.cover_url && <ArticleCover article={article} className="aspect-[16/9] rounded-2xl mb-6 sm:mb-8" />}
        {extractHeadings(article.content).length > 1 && (
          <nav aria-label="Sommaire" className="bg-gray-900/60 border border-gray-800 rounded-xl p-4 sm:p-5 mb-8">
            <p className="font-bold text-white mb-2">Sommaire</p>
            <ol className="list-decimal pl-5 space-y-1 marker:text-yellow-500">
              {extractHeadings(article.content).map(h => (
                <li key={h.id} className={h.level === 3 ? 'ml-4 text-sm' : 'text-sm sm:text-base'}>
                  <a href={`#${h.id}`} className="text-gray-300 hover:text-yellow-400">{h.text}</a>
                </li>
              ))}
            </ol>
          </nav>
        )}
        <Markdown source={article.content} />
      </article>

      {related.length > 0 && (
        <Section title="À lire aussi" className="bg-gray-900/40">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {related.map(a => <ArticleCard key={a.id} article={a} />)}
          </div>
          <div className="mt-4"><ArrowLink to={PAGES.articles}>Tous les articles</ArrowLink></div>
        </Section>
      )}
      <CtaBand />
    </main>
  );
}
